<template>
    <div class="flex justify-between">
        <div class="flex items-center gap-1">
            <label :for="'date-' + model.name">{{ t(`setting.${model.name}.title`) }}</label>
            <UTooltip
                v-if="shouldShowTooltip"
                :text="helpText"
                :delay-duration="0"
                :ui="{
                    content: 'h-fit overflow-visible',
                    text: 'whitespace-pre-line p-1',
                }"
            >
                <i class="i-ph-info inline-block" />
            </UTooltip>
        </div>
        <div>
            <date-picker
                :id="'date-' + model.name"
                v-model="settingValue"
                :year-color-hint="false"
                :disable-background-color-class="true"
                class="w-[200px]"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import {Setting} from '@/setting/setting.service'
import {computed} from 'vue'
import {isValid, parse} from 'date-fns'
import DatePicker from '@/dateUtils/DatePicker.vue'
import {useI18n} from 'vue-i18n'

const {t} = useI18n()

const model = defineModel<Setting<Date>>({required: true})

const helpText = computed(() => {
    return t(`setting.${model.value.name}.tooltip`)
})

const shouldShowTooltip = computed(
    () => helpText.value !== ''
)

const isWithinBounds = (value: Date) => {
    const minDate = model.value.min ? parse(model.value.min.toString(), 'yyyy-MM-dd', new Date()) : null
    const maxDate = model.value.max ? parse(model.value.max.toString(), 'yyyy-MM-dd', new Date()) : null
    if (minDate && value < minDate) {
        return false
    }
    if (maxDate && value > maxDate) {
        return false
    }
    return true
}

const settingValue = computed({
    get: () => {
        const date = model.value.value ?? model.value?.default
        if (!date) {
            return new Date()
        }
        const parsedDate = parse(date.toString(), 'yyyy-MM-dd', new Date())
        return isValid(parsedDate) ? parsedDate : new Date()
    },
    set: (value: Date) => {
        if (isWithinBounds(value)) {
            model.value.value = value
        }
    }
})
</script>

<style scoped>
</style>